import { useEffect, useState } from "react";
import { useCallRecord } from "@/components/calling/CallLink";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { cn, formatINRCompact } from "@/lib/utils";
import { dateLocale, withClock } from "@/lib/dateTime";

interface Suggestion {
  key: string;
  title: string;
  sub: string;
  action: { type: "call" | "whatsapp" | "open"; phone?: string; name?: string; to?: string };
}
interface Context {
  deal: { id: string; name: string; amount: number; expected_close_date: string | null; stage: string; status: string; account: string } | null;
  reading: { proposal: string; total: number; sections: { id: string; title: string; seconds: number }[] } | null;
  last_conversation: { type: string; title: string; text: string; minutes: number | null; at: string } | null;
  suggestions: Suggestion[];
}

const TYPE_LABEL: Record<string, string> = { CALL: "Call", MEETING: "Meeting", EMAIL: "Email", WHATSAPP: "WhatsApp", NOTE: "Note" };
const SECTION = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

function when(iso: string) {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const t = d.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
  if (days < 1) return `today ${t}`;
  return days === 1 ? `yesterday ${t}` : d.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" });
}

const mins = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`);

function LastConversation({ lc }: { lc: NonNullable<Context["last_conversation"]> }) {
  return (
    <div className="flex min-w-[240px] flex-1 flex-col gap-2">
      <h2 className={SECTION}>Last conversation</h2>
      <div className="flex flex-col gap-1 rounded-[10px] bg-panel px-3 py-2.5">
        <span className="text-xs text-muted-foreground">
          {TYPE_LABEL[lc.type] || lc.type} · {when(lc.at)}
          {lc.minutes ? ` · ${lc.minutes} min` : ""}
        </span>
        <span className="text-sm">{lc.text || lc.title}</span>
      </div>
    </div>
  );
}

/** The deal behind a queue item: where it stands, what to do next, and how the last conversation went */
export function DealContextPanel({ dealId, onWhatsApp }: { dealId: string; onWhatsApp: (phone: string, name: string, dealId: string) => void }) {
  const callRecord = useCallRecord();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<Context | null>(null);

  useEffect(() => {
    let alive = true;
    setCtx(null);
    api
      .get(`/api/MyDay/DealContext/${dealId}`)
      .then((r) => alive && setCtx(unwrapObject<Context>(r)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [dealId]);

  if (!ctx?.deal) return null;
  const d = ctx.deal;
  const lc = ctx.last_conversation;
  const reading = ctx.reading;

  const run = (s: Suggestion) => {
    const a = s.action;
    if (a.type === "call" && a.phone) void callRecord("DEAL", dealId, a.phone);
    else if (a.type === "whatsapp" && a.phone) onWhatsApp(a.phone, a.name || "", dealId);
    else if (a.to) navigate(a.to);
  };

  return (
    <>
      <Link to={`/deals/${d.id}`} className="flex flex-wrap items-center gap-3 rounded-[10px] border border-border px-3.5 py-3 hover:bg-muted/40">
        <span className="flex size-[34px] items-center justify-center rounded-lg bg-sidebar text-white">
          <Briefcase className="size-4" />
        </span>
        <span className="flex min-w-[180px] flex-1 flex-col leading-tight">
          <span className="font-semibold">{d.name}</span>
          <span className="text-xs text-muted-foreground">
            {[d.stage, d.expected_close_date ? `closes ${new Date(d.expected_close_date).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}` : null, "open the deal"].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className="font-mono text-[17px] font-semibold">{formatINRCompact(d.amount)}</span>
      </Link>

      {ctx.suggestions.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className={SECTION}>Suggested next step</h2>
          <div className="flex flex-wrap gap-2.5">
            {ctx.suggestions.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => run(s)}
                className={cn(
                  "flex min-w-[170px] flex-1 flex-col items-start gap-0.5 rounded-[10px] px-3.5 py-3 text-left transition-colors",
                  i === 0 ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border bg-card hover:bg-muted",
                )}
              >
                <span className="font-semibold">{s.title}</span>
                <span className={cn("text-xs", i === 0 ? "text-primary-foreground/80" : "text-muted-foreground")}>{s.sub}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {(reading || lc) && (
        <div className="flex flex-wrap gap-[18px]">
          {reading && (
            <div className="flex min-w-[240px] flex-1 flex-col gap-2">
              <h2 className={SECTION}>What they read</h2>
              {reading.sections.slice(0, 5).map((r, i) => (
                <div key={r.id} className="grid grid-cols-[104px_minmax(0,1fr)_54px] items-center gap-2.5 text-[13px]">
                  <span className="truncate text-foreground/80">{r.title}</span>
                  <span className="h-2 rounded-full bg-muted" role="img" aria-label={`${r.title}: ${mins(r.seconds)}`}>
                    <span className={cn("block h-full rounded-full", i === 0 ? "bg-gold" : "bg-primary")} style={{ width: `${Math.max(6, Math.round((r.seconds / reading.sections[0]!.seconds) * 100))}%` }} />
                  </span>
                  <span className="text-right font-mono text-xs">{mins(r.seconds)}</span>
                </div>
              ))}
            </div>
          )}
          {lc && <LastConversation lc={lc} />}
        </div>
      )}
    </>
  );
}
